import { useEffect, useLayoutEffect, type RefObject } from 'react'

/**
 * Поле ввода растёт вместе с текстом — и от переносов строк, и от длинных строк — до потолка
 * из CSS (max-height), дальше прокручивается. Когда текст стёрт или отправлен, сжимается обратно.
 *
 * field-sizing: content сделал бы это сам, но в Safari и Firefox его нет. Высоту меряем
 * на скрытом двойнике, а не на самом поле: сброс поля в height: auto на миг схлопывает его,
 * лента сообщений над ним вырастает, браузер обрезает её прокрутку — и лента прыгает вверх.
 */
export function useAutosize(ref: RefObject<HTMLTextAreaElement | null>, value: string): void {
  useLayoutEffect(() => {
    const el = ref.current
    if (el) fit(el)
  }, [ref, value])

  // Ширина поля меняется вместе с окном и колонками, а с ней — переносы.
  // Шрифт догружается позже первой отрисовки, и строки в нём другой длины.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let width = el.offsetWidth
    let maxHeight = getComputedStyle(el).maxHeight
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      const nextMax = getComputedStyle(el).maxHeight
      if (el.offsetWidth === width && nextMax === maxHeight) return
      width = el.offsetWidth
      maxHeight = nextMax
      fit(el)
    })
    observer?.observe(el)
    let alive = true
    void document.fonts?.ready.then(() => {
      if (alive) fit(el)
    })
    return () => {
      alive = false
      observer?.disconnect()
    }
  }, [ref])
}

// всё, от чего зависит, сколько строк займёт текст
const SIZING = [
  'box-sizing', 'width', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width', 'border-style',
  'font-family', 'font-size', 'font-style', 'font-weight', 'font-stretch', 'font-variant', 'font-feature-settings',
  'letter-spacing', 'word-spacing', 'line-height', 'text-indent', 'text-transform', 'text-rendering',
  'tab-size', 'white-space', 'word-break', 'overflow-wrap', 'scrollbar-gutter',
]

let twin: HTMLTextAreaElement | null = null

function fit(el: HTMLTextAreaElement) {
  const style = getComputedStyle(el)
  if (!twin) {
    twin = document.createElement('textarea')
    twin.setAttribute('aria-hidden', 'true')
    twin.tabIndex = -1
    document.body.appendChild(twin)
  }
  for (const name of SIZING) twin.style.setProperty(name, style.getPropertyValue(name))
  // высота 0: scrollHeight двойника — ровно высота текста с отступами
  twin.style.setProperty('position', 'absolute')
  twin.style.setProperty('top', '0')
  twin.style.setProperty('left', '-9999px')
  twin.style.setProperty('visibility', 'hidden')
  twin.style.setProperty('overflow', 'hidden')
  twin.style.setProperty('height', '0')
  twin.style.setProperty('min-height', '0')
  twin.style.setProperty('max-height', 'none')
  twin.style.setProperty('pointer-events', 'none')
  twin.value = el.value || ' '

  const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
  const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
  const needed = style.boxSizing === 'border-box' ? twin.scrollHeight + border : twin.scrollHeight - padding
  const max = parseFloat(style.maxHeight)
  const over = Number.isFinite(max) && needed > max
  el.style.height = `${over ? max : needed}px`
  // полоса прокрутки — только когда текст правда не влез
  el.style.overflowY = over ? 'auto' : 'hidden'
}
